/**
 * DemoPortalWidget — Batch #14.232
 *
 * Widget « Portail démo » pour le dashboard configurable :
 *   1. KPIs de visites du portail démo (24 h / 7 j / 30 j + dernière visite)
 *   2. Derniers prospects reçus via le formulaire « Devenir client » du démo,
 *      avec TOUTES les infos du formulaire (dépliables).
 *
 * Sources : GET /admin/demo-stats + GET /leads (filtre source=demo_portal).
 */
import { useEffect, useState, useCallback } from "react";
import { Eye, Clock, TrendingUp, Users, Sparkles, Calendar, ChevronDown, ChevronRight } from "lucide-react";
import { api } from "@/api";

function relTime(iso) {
  if (!iso) return "—";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "à l'instant";
  if (diff < 3600) return `il y a ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `il y a ${Math.floor(diff / 3600)} h`;
  if (diff < 86400 * 7) return `il y a ${Math.floor(diff / 86400)} j`;
  return new Date(iso).toLocaleDateString("fr-CA");
}

const METHODE_LABELS = { email: "Courriel", telephone: "Téléphone", sms: "SMS / texto" };

function Kpi({ label, value, icon: Icon, pulse }) {
  return (
    <div className="bg-indigo-50/70 border border-indigo-100 rounded-lg px-2 py-1.5 text-center">
      <div className="flex items-center justify-center gap-1 text-[9px] uppercase font-bold text-indigo-500">
        <Icon className={`w-2.5 h-2.5 ${pulse ? "animate-pulse text-rose-500" : ""}`} />{label}
      </div>
      <div className="text-lg font-bold text-indigo-900 leading-tight">{value ?? 0}</div>
    </div>
  );
}

function LeadDetail({ label, value }) {
  if (!value) return null;
  return (
    <div className="text-[10px]">
      <span className="font-bold text-slate-500 uppercase">{label} : </span>
      <span className="text-slate-700">{value}</span>
    </div>
  );
}

function DemoLeadRow({ lead }) {
  const [open, setOpen] = useState(false);
  const isNew = lead.status === "nouveau";
  return (
    <div
      className={`border rounded-lg p-2 ${isNew ? "bg-amber-50/60 border-amber-200" : "bg-white border-slate-100"}`}
      data-testid={`demo-widget-lead-${lead.id}`}
    >
      <button onClick={() => setOpen(!open)} className="w-full flex items-start gap-1.5 text-left">
        {open ? <ChevronDown className="w-3 h-3 mt-0.5 text-slate-400 shrink-0" /> : <ChevronRight className="w-3 h-3 mt-0.5 text-slate-400 shrink-0" />}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="text-xs font-bold text-slate-800 truncate">{lead.nom}</span>
            {isNew && <span className="text-[8px] font-bold uppercase bg-amber-200 text-amber-800 rounded px-1 py-px shrink-0">Nouveau</span>}
            {lead.rdv_demande && (
              <span className="inline-flex items-center gap-0.5 text-[8px] font-bold uppercase bg-violet-100 text-violet-700 rounded px-1 py-px shrink-0">
                <Calendar className="w-2 h-2" /> RDV
              </span>
            )}
          </div>
          <div className="text-[10px] text-slate-500 truncate">
            {lead.phone} · {lead.email}
          </div>
        </div>
        <span className="text-[9px] text-slate-400 shrink-0">{relTime(lead.created_at)}</span>
      </button>
      {open && (
        <div className="mt-1.5 ml-4 space-y-0.5 border-t border-slate-100 pt-1.5" data-testid={`demo-widget-lead-${lead.id}-details`}>
          <LeadDetail label="Adresse" value={lead.adresse} />
          <LeadDetail label="Contact préféré" value={METHODE_LABELS[lead.methode_contact] || lead.methode_contact} />
          <LeadDetail label="Moment" value={lead.moment_contact} />
          <LeadDetail label="RDV" value={lead.rdv_demande ? (lead.rdv_format === "presentiel" ? "Oui — en présence" : "Oui — Google Meet") : null} />
          <LeadDetail label="Secteur" value={lead.secteur_expertise} />
          <LeadDetail label="Note" value={lead.message} />
        </div>
      )}
    </div>
  );
}

export default function DemoPortalWidget() {
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [s, l] = await Promise.all([
        api.get("/admin/demo-stats").catch(() => ({ data: null })),
        api.get("/leads").catch(() => ({ data: { items: [] } })),
      ]);
      setStats(s.data);
      setLeads((l.data?.items || []).filter((x) => x.source === "demo_portal").slice(0, 5));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  if (loading) return <div className="text-center text-gray-300 text-sm py-4">·</div>;

  const newCount = leads.filter((l) => l.status === "nouveau").length;

  return (
    <div className="animate-fade-in space-y-2.5" data-testid="widget-demo-portal">
      {/* Visites */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <span className="text-[10px] font-bold uppercase text-slate-400 flex items-center gap-1">
            <Eye className="w-3 h-3" /> Visites du démo
          </span>
          <span className="text-[9px] text-slate-400">
            Dernière : {relTime(stats?.last_visit_at)}
          </span>
        </div>
        <div className="grid grid-cols-3 gap-1.5">
          <Kpi label="24 h" value={stats?.last_24h} icon={Clock} pulse={(stats?.last_hour || 0) > 0} />
          <Kpi label="7 j" value={stats?.last_7d} icon={TrendingUp} />
          <Kpi label="30 j" value={stats?.last_30d} icon={Users} />
        </div>
      </div>

      {/* Leads démo */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <span className="text-[10px] font-bold uppercase text-slate-400 flex items-center gap-1">
            <Sparkles className="w-3 h-3" /> Prospects du démo
          </span>
          {newCount > 0 && (
            <span className="text-[9px] font-bold bg-amber-100 text-amber-700 rounded-full px-1.5 py-px"
                  data-testid="demo-widget-new-count">
              {newCount} nouveau{newCount > 1 ? "x" : ""}
            </span>
          )}
        </div>
        {leads.length === 0 ? (
          <div className="text-[11px] text-slate-400 italic py-1.5 text-center">
            Aucun prospect du démo pour l'instant
          </div>
        ) : (
          <div className="space-y-1.5 max-h-[220px] overflow-y-auto" data-testid="demo-widget-leads-list">
            {leads.map((l) => <DemoLeadRow key={l.id} lead={l} />)}
          </div>
        )}
      </div>
    </div>
  );
}
